import { OBJECTIVES_FEATURE_COPY } from '@/shared/resourceModuleParity'
import type { ObjectiveWithProgress } from '@/types/objective'
import type { ProgressBarVariant } from '@/shared/ui/ProgressBar'

import { type Goal, isGoalCompleted, isGoalStarted } from './milestones'

export type ObjectiveProgress = {
  sumCurrent: number
  sumTarget: number
  percent: number
}

/**
 * Progresso agregado de um objetivo.
 *
 * `conditions_progress` só descreve o que o usuário já tocou — para um objetivo
 * ainda não iniciado a api pode devolver a lista vazia. Nesse caso a meta vem
 * de `objective_conditions`, senão o card mostraria "0 / 0" e leria como
 * objetivo sem condição alguma.
 */
export function getProgressData(
  objective: ObjectiveWithProgress
): ObjectiveProgress {
  const cps = objective.conditions_progress ?? []

  if (!cps.length) {
    const sumTarget = (objective.objective_conditions ?? []).reduce(
      (acc, condition) => acc + (condition.quantity ?? 0),
      0
    )
    return { sumCurrent: 0, sumTarget, percent: 0 }
  }

  const sumCurrent = cps.reduce(
    (acc, cp) => acc + Math.min(cp.count, cp.required),
    0
  )
  const sumTarget = cps.reduce((acc, cp) => acc + cp.required, 0)
  const percent = sumTarget > 0 ? Math.round((sumCurrent / sumTarget) * 100) : 0

  return { sumCurrent, sumTarget, percent }
}

export function progressVariant(
  completed: boolean,
  percent: number
): ProgressBarVariant {
  if (completed) return 'success'
  if (percent > 0) return 'progress'
  return 'primary'
}

export function progressLabel(
  completed: boolean,
  sumCurrent: number,
  sumTarget: number
): string {
  if (completed) return OBJECTIVES_FEATURE_COPY.completedLabel()
  if (sumTarget > 0) {
    return `${sumCurrent} de ${sumTarget} concluídos`
  }
  return OBJECTIVES_FEATURE_COPY.notStartedLabel()
}

/** Objetivo que o usuário ainda pode conquistar. */
export const isPending = (objective: ObjectiveWithProgress) =>
  !objective.completed

/** Objetivo pendente que o usuário já começou. */
export const isInProgress = (objective: ObjectiveWithProgress) =>
  isPending(objective) && getProgressData(objective).percent > 0

export type ObjectiveListGroupStatus = 'progress' | 'available' | 'success'

export type ObjectiveListGroup = {
  status: ObjectiveListGroupStatus
  label: string
  items: Goal[]
}

const byGoalPosition = (a: Goal, b: Goal) => a.position - b.position

/**
 * Agrupa **metas** para a listagem: em andamento primeiro, depois próximas
 * (não iniciadas) e concluídas por último.
 *
 * Opera sobre metas, e não sobre objetivos crus, por uma razão de leitura: uma
 * escada com 2 de 15 marcos conquistados precisa aparecer **inteira** em "Em
 * andamento". Se a partição fosse por objetivo, os dois marcos já conquistados
 * cairiam em "Concluídos" e a mesma escada apareceria nas duas seções ao mesmo
 * tempo — pior que o problema original.
 */
export function partitionObjectives(goals: Goal[]): ObjectiveListGroup[] {
  const inProgress: Goal[] = []
  const upcoming: Goal[] = []
  const completed: Goal[] = []

  for (const goal of goals) {
    if (isGoalCompleted(goal)) {
      completed.push(goal)
      continue
    }

    if (isGoalStarted(goal)) {
      inProgress.push(goal)
      continue
    }

    upcoming.push(goal)
  }

  inProgress.sort(byGoalPosition)
  upcoming.sort(byGoalPosition)
  completed.sort(byGoalPosition)

  const groups: ObjectiveListGroup[] = []

  if (inProgress.length > 0) {
    groups.push({
      status: 'progress',
      label: 'Em andamento',
      items: inProgress
    })
  }

  if (upcoming.length > 0) {
    groups.push({
      status: 'available',
      label: 'Próximas metas',
      items: upcoming
    })
  }

  if (completed.length > 0) {
    groups.push({
      status: 'success',
      label: 'Concluídos',
      items: completed
    })
  }

  return groups
}

/**
 * Metas que ainda não foram concluídas, na ordem em que o produtor as definiu.
 *
 * Meta não iniciada continua na lista — ela é justamente o que o participante
 * precisa ver para saber o que fazer a seguir.
 */
export function selectPendingObjectives(goals: Goal[]): Goal[] {
  return goals.filter(goal => !isGoalCompleted(goal)).sort((a, b) => {
    const startedDiff = Number(isGoalStarted(b)) - Number(isGoalStarted(a))
    if (startedDiff !== 0) return startedDiff

    return a.position - b.position
  })
}

/** Recorte de `selectPendingObjectives` para o widget da home. */
export function selectObjectivesPreview(goals: Goal[], limit = 3): Goal[] {
  if (limit <= 0) return []
  return selectPendingObjectives(goals).slice(0, limit)
}
